import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import {BulkChangePriceDashboardDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import IconButton from "../../../../../../components/base/button/icon_button";
import {Add, OpenInNew} from "@mui/icons-material";
import {Tooltip, useTheme} from "@mui/material";
import {Col, Row} from "reactstrap";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import Button from "../../../../../../components/base/button/button";
import BulkPriceChangeSearchSection from "../../../../../../components/search-sections/bulk-price-change";
import LineChart from "../../../../../../components/base/chart/line-chart";
import Paper from "@mui/material/Paper";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import Api from "../../../../../../../core/services/api/api";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import {Link, useHistory} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {NewBulkPriceChangeServerStatusIds} from "../index";

const exportingChunkSize = 100;

const dataGridSaveConfig = {
    name: 'BulkPriceChangeDashboardViewDataGrid',
    version: 1,
}

const BulkPriceChangeDashboard = () => {
    const history = useHistory()
    const [creating, setCreating] = useState(false);
    const [data, setData] = useState([]);
    const [chartData, setChartData] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('bulk-price-change-dashboard-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportBulkPriceChangeDashboard = operatorAccessLevels.packages.lookup.exportBulkPriceChangeDashboard;

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.bulkPriceChangeDashboard);
        configureBehavioralTool((tags) => tags.bulkPriceChangeDashboard);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: BulkChangePriceDashboardDataGridCellKeys.viewReports,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: 0,
            },
            {
                title: "Submit Date & Time",
                name: BulkChangePriceDashboardDataGridCellKeys.submitDateTime,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.dateTime,
                width: 150,
                sortable: false,
            },
            {
                title: "Operator",
                name: BulkChangePriceDashboardDataGridCellKeys.operator,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 150,
            },
            {
                title: "Status",
                name: BulkChangePriceDashboardDataGridCellKeys.status,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
            },
            {
                title: "Affected Parts",
                name: BulkChangePriceDashboardDataGridCellKeys.affectedParts,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 120,
                sortable: false,
            },
            {
                title: "Affected Price Levels",
                name: BulkChangePriceDashboardDataGridCellKeys.affectedPriceLevels,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 150,
                sortable: false,
            },
            {
                title: "Changed Amount",
                name: BulkChangePriceDashboardDataGridCellKeys.changedAmount,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.number,
                width: 140,
                sortable: false,
            },
        ], [])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Gets the bulk price change data from server
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            operatorId: args.filters.operator,
            startDate: args.filters.startDate,
            endDate: args.filters.endDate,
            pagination: args.paginationInfo,
        };
        const response = await Api.getBulkPriceChangeDashboardInformation(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response?.data?.requests?.items ?? []);
            setChartData(response?.data?.reports ?? [])
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.requests?.totalCount,
            }))
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted]);

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Formats the exported data's entries and returns the info to be exported.
     * @param {any[]} list
     * @return {DataGridExportInfo}
     */
    const formatExportingData = useCallback((list) => {
        return {
            title: `Bulk Price Change Reports`,
            body: list?.map(item => ({
                "Submit Date & Time": item?.submitDateTime ? DateUtils.serverToLocal(item?.submitDateTime).format(DateUtils.dateTimeFormat) : '',
                "Operator": item?.operatorName,
                "Status": item?.requestStatusTitle,
                "Affected Parts": item?.affectedParts,
                "Changed Amount": !item.isPercentage
                    ? `${item.requestedAmount > 0 ? '+' : '-'}` + formatMoney(Math.abs(item.requestedAmount))
                    : `${item.requestedAmount > 0 ? '+' : ''}` + item.requestedAmount?.toFixed(2) + '%',
                "Affected Price Levels": item?.affectedPriceLevels,
            })) ?? [],
        }
    }, [])

    /**
     * Exports the entries of this view by searching over all the entries in the system.
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])

    /**
     * Creates a new bulk price change entity in the server.
     *
     * * if the response of the api is successful, navigates the user to the newly created bulk price change.
     * @return void
     */
    const createNewBulkPriceChange = async () => {
        setCreating(true);
        const response = await Api.createBulkPriceChange();
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag) {
            history.push(routeFunctions.products.bulkPriceChange.newBulkPrice(response.data?.id));
        }
    }

    /**
     * Memo version of table row records used in data grid
     */
    const rows = useMemo(() => {
        return data?.map(item => ({
            key: item.id,
            data: item,
            cells: {
                [BulkChangePriceDashboardDataGridCellKeys.viewReports]:
                    <Tooltip
                        title={'View'}
                        placement={'bottom'}
                    >
                        <IconButton
                            color={'secondary'}
                            component={Link}
                            to={
                                item.requestStatusId === NewBulkPriceChangeServerStatusIds.finalized
                                    ? routeFunctions.products.bulkPriceChange.report(item.id)
                                    : routeFunctions.products.bulkPriceChange.newBulkPrice(item.id)
                            }
                        >
                            <OpenInNew className='icon normal'/>
                        </IconButton>
                    </Tooltip>,
                [BulkChangePriceDashboardDataGridCellKeys.submitDateTime]: item?.submitDateTime,
                [BulkChangePriceDashboardDataGridCellKeys.operator]: item?.operatorName,
                [BulkChangePriceDashboardDataGridCellKeys.status]: item?.requestStatusTitle,
                [BulkChangePriceDashboardDataGridCellKeys.affectedParts]: item?.affectedParts,
                [BulkChangePriceDashboardDataGridCellKeys.changedAmount]:
                    !item.isPercentage
                        ? `${item.requestedAmount > 0 ? '+' : '-'}` + formatMoney(Math.abs(item.requestedAmount))
                        : `${item.requestedAmount > 0 ? '+' : ''}` + item.requestedAmount?.toFixed(2) + '%',
                [BulkChangePriceDashboardDataGridCellKeys.affectedPriceLevels]: item?.affectedPriceLevels,

            }
        }))
    }, [data])


    return (
        <>
            <Row className='no-gutters w-100 d-flex justify-content-between align-items-start my-4 mt-3'>
                <h2 className={'text-xl font-weight-semi-bold m-0 ml-3'}>
                    Bulk Price Change
                </h2>
                <Button
                    disabled={searchData.loading}
                    style={{height: '45px'}}
                    className={'ml-3 d-flex justify-content-center align-items-center'}
                    variant={'outlined'}
                    color={'primary'}
                    onClick={() => createNewBulkPriceChange()}
                >
                    <Add className='icon small'/>
                    <p className='ml-2'>
                        {
                            creating
                                ? "Creating..."
                                : "Add a New Bulk Change"
                        }

                    </p>
                </Button>
            </Row>
            <BulkPriceChangeSearchSection searchData={searchData}/>
            <div className={'mb-5'}/>
            <Paper className={'paper mx-2 mt-4 px-3 d-flex justify-content-center'}>
                <ChartSection dataset={chartData} isLoading={searchData.loading}/>
            </Paper>
            <div className={'mb-5'}/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={rows}
                hideFooterActions={true}
                state={{
                    sortBy: searchData.orderBy,
                    pagination: searchData.paginationInfo,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                getExportInfo={canExportBulkPriceChangeDashboard ? exportToCsv : undefined}
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
        </>
    );
}

const ChartSection = ({dataset, isLoading}) => {
    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])

    /**
     * Compares strings in format of 'MM/DD' based on the date they represent
     * @param dateStringOne
     * @param dateStringTwo
     * @return {number}
     */
    const dateStringCompare = (dateStringOne, dateStringTwo) => {
        if (parseInt(dateStringOne.split('/')[1]) < parseInt(dateStringTwo.split('/')[1]))
            return -1;
        if (parseInt(dateStringOne.split('/')[1]) === parseInt(dateStringTwo.split('/')[1]) && parseInt(dateStringOne.split('/')[0]) < parseInt(dateStringTwo.split('/')[0]))
            return -1;
        if (parseInt(dateStringOne.split('/')[1]) === parseInt(dateStringTwo.split('/')[1]) && parseInt(dateStringOne.split('/')[0]) === parseInt(dateStringTwo.split('/')[0]))
            return 0;
        return 1;
    }

    /**
     * Aggregates data based on date
     * @param data
     * @return {{}}
     */
    const getDatasetData = useCallback((data = []) => {
        const aggregated = {};
        data?.map(e => ({date: DateUtils.serverToLocal(e.submitDateTime).format("DD/MM"), amount: e?.averagePrice ?? 0}))
            ?.sort((a, b) => dateStringCompare(a?.date, b?.date))
            ?.forEach(record => {
                aggregated[record.date] = !!aggregated[record.date]
                    ? parseFloat(aggregated[record.date]) + parseFloat((record?.amount ?? 0))
                    : parseFloat((record?.amount ?? 0))
            })
        return aggregated;
    }, [])

    /**
     * Memo version of chart data passed to the
     * @type {{datasets: *[], labels: *[]}}
     */
    const chartData = useMemo(() => {
        const chartData = {
            datasets: [],
            labels: [],
        }
        const data = getDatasetData(dataset);
        chartData.datasets.push({
            data: data,
            label: 'Price Changes',
            formatValue: formatMoney,
            borderColor: chartColors[0],
            pointBackgroundColor: chartColors[0],
            pointBorderColor: chartColors[0],
            borderWidth: 1,
            cubicInterpolationMode: 'monotone',
        });
        chartData.labels = [...new Set([...chartData.labels, ...Object.keys(data)])].sort((a, b) => dateStringCompare(a, b));

        return chartData;

    }, [chartColors, dataset, getDatasetData]);

    return (
        <>
            {
                isLoading
                    ? <div className={'loading bulk-price-change-chart-section'}>
                        <div/>
                    </div>
                    :
                    <Col
                        xs={12}
                        className={'my-4  transition mx-auto bar-chart'}
                    >
                        <LineChart height={window.innerHeight / 2} data={chartData}/>
                    </Col>
            }
        </>
    );
}

export default BulkPriceChangeDashboard;
